/**
 * CardDiscoveryPanel - Deck wrapper over the shared `CardSearchPanel`
 *
 * Adds the deck-specific target controls (board selector) and row rendering.
 */
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import CardSearchPanel from '@/components/CardSearchPanel/CardSearchPanel';
import { type DeckBoard, selectedBoardAtom } from '@/features/decks/state/deckEditorSettings';
import { getSelectableZones } from '@/lib/deckValidation';
import type { MinimalCard } from '@/services/cardDatabase';
import type { DeckFormat, DeckZone } from '@/types/deck';

import CardDiscoveryRow from './CardDiscoveryRow';

export interface CardDiscoveryPanelProps {
  deckId: string;
  deckFormat: DeckFormat | null;
  deckStatus: 'brewing' | 'active' | 'archived';
  onAddCard: (scryfallId: string, quantity: number, zone: DeckZone) => void;
  /** Called when a card is selected to open details panel */
  onCardSelect?: (scryfallId: string) => void;
  onClose?: () => void;
  className?: string;
}

const BOARD_LABELS: Record<DeckBoard, string> = {
  main: 'Main Deck',
  sideboard: 'Sideboard',
  maybeboard: 'Maybeboard',
  tokens: 'Tokens',
};

export default function CardDiscoveryPanel({
  deckId,
  deckFormat,
  deckStatus,
  onAddCard,
  onCardSelect,
  onClose,
  className,
}: CardDiscoveryPanelProps): ReactElement {
  const globalSelectedBoard = useAtomValue(selectedBoardAtom);
  const [targetBoard, setTargetBoard] = useState<DeckBoard>(globalSelectedBoard);

  useEffect(() => {
    setTargetBoard(globalSelectedBoard);
  }, [globalSelectedBoard]);

  const validBoards = useMemo((): DeckBoard[] => getSelectableZones(deckFormat, deckStatus) as DeckBoard[], [deckFormat, deckStatus]);

  useEffect(() => {
    if (!validBoards.includes(targetBoard)) {
      setTargetBoard('main');
    }
  }, [validBoards, targetBoard]);

  const addCard = (card: MinimalCard) => onAddCard(card.id, 1, targetBoard);

  return (
    <CardSearchPanel
      className={className}
      onClose={onClose}
      legalityFormat={deckFormat}
      onActivate={addCard}
      targetControls={
        <div className="flex items-center gap-2">
          <label htmlFor="board-select" className="text-ash text-xs">
            Add to:
          </label>
          <select
            id="board-select"
            value={targetBoard}
            onChange={(e) => setTargetBoard(e.target.value as DeckBoard)}
            className="bg-slate border-storm text-pearl focus-ring rounded-md border px-2 py-1 text-sm"
          >
            {validBoards.map((board) => (
              <option key={board} value={board}>
                {BOARD_LABELS[board]}
              </option>
            ))}
          </select>
        </div>
      }
      renderRow={({ card, isHighlighted, onMouseEnter }) => (
        <CardDiscoveryRow
          card={card}
          deckId={deckId}
          deckFormat={deckFormat}
          onAdd={addCard}
          onCardSelect={onCardSelect}
          isHighlighted={isHighlighted}
          onMouseEnter={onMouseEnter}
        />
      )}
    />
  );
}
